/**
 * Phase 2 — entity labels + route map shared by the list panes, note links,
 * and the Ctrl+K palette. Pure functions over campaign data (no side effects).
 */
import type { Campaign, NoteLinkKind } from '@/domain/campaign';
import type { Locale } from '@/domain/shared';
import { localized, translate } from '@/i18n';
import type { RouteId } from '@/state/store';

/** Everything the search palette can find. */
export type EntityKind = NoteLinkKind | 'campaign' | 'note';

/** Which section of the app shows a given entity kind. */
export const ROUTE_FOR_KIND: Record<EntityKind, RouteId> = {
  campaign: 'cockpit',
  session: 'sessions',
  character: 'characters',
  npc: 'npcs',
  encounter: 'encounters',
  location: 'world',
  faction: 'world',
  item: 'world',
  'story-node': 'story',
  note: 'notes',
};

function findIn<T extends { id: string }>(list: T[], id: string): T | undefined {
  return list.find((x) => x.id === id);
}

/** Human title for any campaign entity, or null if it no longer exists. */
export function entityTitle(
  campaign: Campaign,
  kind: EntityKind,
  id: string,
  locale: Locale,
): string | null {
  switch (kind) {
    case 'campaign':
      return campaign.id === id ? campaign.name : null;
    case 'session': {
      const s = findIn(campaign.sessions, id);
      if (!s) return null;
      return `#${s.number} · ${localized(s.title, locale) || translate(locale, 'sessions.untitled')}`;
    }
    case 'character':
      return findIn(campaign.characters, id)?.name ?? null;
    case 'npc':
      return findIn(campaign.npcs, id)?.name ?? null;
    case 'encounter':
      return findIn(campaign.encounters, id)?.name ?? null;
    case 'location':
      return findIn(campaign.locations, id)?.name ?? null;
    case 'faction':
      return findIn(campaign.factions, id)?.name ?? null;
    case 'item': {
      const item = findIn(campaign.items, id);
      return item ? localized(item.name, locale) : null;
    }
    case 'story-node':
      return findIn(campaign.storyNodes, id)?.title ?? null;
    case 'note': {
      const note = findIn(campaign.notes, id);
      if (!note) return null;
      return note.title || translate(locale, 'notes.untitled');
    }
  }
}

export interface LinkTarget {
  id: string;
  label: string;
}

/** Pickable targets for one note-link kind (used by the note editor). */
export function linkTargets(
  campaign: Campaign,
  kind: NoteLinkKind,
  locale: Locale,
): LinkTarget[] {
  switch (kind) {
    case 'character':
      return campaign.characters.map((c) => ({ id: c.id, label: c.name }));
    case 'npc':
      return campaign.npcs.map((n) => ({ id: n.id, label: n.name }));
    case 'location':
      return campaign.locations.map((l) => ({ id: l.id, label: l.name }));
    case 'encounter':
      return campaign.encounters.map((e) => ({ id: e.id, label: e.name }));
    case 'session':
      return [...campaign.sessions]
        .sort((a, b) => a.number - b.number)
        .map((s) => ({
          id: s.id,
          label: `#${s.number} · ${localized(s.title, locale) || translate(locale, 'sessions.untitled')}`,
        }));
    case 'faction':
      return campaign.factions.map((f) => ({ id: f.id, label: f.name }));
    case 'story-node':
      return campaign.storyNodes.map((s) => ({ id: s.id, label: s.title }));
    case 'item':
      return campaign.items.map((i) => ({ id: i.id, label: localized(i.name, locale) }));
  }
}
